import { useReactiveVar } from '@apollo/client';
import Brightness2Icon from '@mui/icons-material/Brightness2';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import { alpha, AppBar, Box, Container, IconButton, Stack, Toolbar } from '@mui/material';
import { config, isCurrentAppTheme, themeConfig } from 'appConfig';
import { darkThemeVar, userThemeVar } from 'client/cache';
import { NotificationBell } from 'components/Notifications/NotificationBell';
import { useFeatureFlags } from 'featureFlags/useFeatureFlags';
import { ReactComponent as AppLogo } from 'images/app-logo.svg';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { Link } from 'react-router';
import { PartnersThemeDecider } from 'shared/PartnersThemeDecider';
import { isNull } from 'typeDeclarations/typeGuards';
import { EnvironmentLabel } from './EnvironmentLabel';
import { HeaderMenuToggle } from './HeaderMenuToggle/HeaderMenuToggle';

export const APP_HEADER_ID = 'app-header';

export const Header: FunctionComponent = () => {
  const darkTheme = useReactiveVar(darkThemeVar);
  const userTheme = useReactiveVar(userThemeVar);
  const { isFeatureEnabled } = useFeatureFlags();

  const handleThemeToggle = useCallback(() => {
    darkThemeVar(!darkThemeVar());
  }, []);

  const logoSource = useMemo(() => {
    if (isNull(userTheme)) return null;
    return `${config.whitelabel_url}${userTheme}/images/${
      themeConfig[userTheme].altLogo ? 'app-logo-alt' : 'app-logo'
    }.svg`;
  }, [userTheme]);

  // Header background is set here, not in theme.ts, because AuthPage also uses AppBar.
  // The original default color was: `linear-gradient(127deg, ${theme.palette.primary.main} 3.5%, #D44C80 145.64%)`
  // TODO: fix this after implementing the new Design System.
  const backgroundStyle = useMemo(() => {
    if (userTheme === 'robin' || isCurrentAppTheme('robin')) {
      return {
        // MUI AppBar paints `background-color: var(--AppBar-background)`.
        '--AppBar-background': '#dffe5b',
        '--AppBar-color': '#14332d',
        background: '#dffe5b',
        color: '#14332d',
      };
    }

    if (isCurrentAppTheme('default') && userTheme === 'default') {
      return {
        background: 'linear-gradient(127deg, coral 3.5%, #D44C80 145.64%)',
      };
    }

    return {};
  }, [userTheme]);

  return (
    <AppBar elevation={0} position="sticky" id={APP_HEADER_ID} sx={backgroundStyle}>
      <Toolbar disableGutters sx={{ minHeight: { xs: '4rem', md: '5rem' } }}>
        <Container>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" columnGap={1}>
              <Link to="/campaigns">
                <Box
                  sx={{
                    width: 'auto',
                    height: {
                      xs: '2rem',
                      md: '3rem',
                    },
                  }}
                >
                  {/* TODO: Investigate this. We do a nullability check, but `partnerTheme` is not typed as nullable. */}
                  {isNull(userTheme) || isNull(logoSource) ? (
                    <AppLogo width="100%" height="100%" />
                  ) : (
                    <img width="100%" height="100%" alt="App Logo" src={logoSource} />
                  )}
                </Box>
              </Link>
              <EnvironmentLabel />
            </Stack>
            <Stack direction="row" alignItems="center" columnGap={3}>
              {isFeatureEnabled('dark-theme') && (
                <IconButton
                  edge="start"
                  onClick={handleThemeToggle}
                  sx={{
                    color: 'inherit',
                    ':hover': {
                      backgroundColor: ({ palette }) => alpha(palette.common.white, 0.15),
                    },
                  }}
                >
                  {!darkTheme ? <Brightness2Icon fontSize="small" /> : <WbSunnyIcon fontSize="small" />}
                </IconButton>
              )}
              <NotificationBell />
              <PartnersThemeDecider fallback={<HeaderMenuToggle />} />
            </Stack>
          </Stack>
        </Container>
      </Toolbar>
    </AppBar>
  );
};
